import { t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { attachWhoTooltip } from './admin-who';

// ============================================================
// Краткие реакции на игрока внутри самой карточки (топ-4 чипа).
// Модуль без побочных эффектов: используется и на сайте
// (ui/player-reactions.ts), и в оверлее OBS (overlay.ts).
// ============================================================

export type ReactionTone = 'positive' | 'neutral' | 'negative';

export interface ReactionDef {
  kind: string;
  icon: string;
  tone: ReactionTone;
  key: TranslationKey;
}

export const PLAYER_REACTIONS: ReactionDef[] = [
  { kind: 'love', icon: '❤️', tone: 'positive', key: 'player.reaction.love' },
  { kind: 'respect', icon: '👑', tone: 'positive', key: 'player.reaction.respect' },
  { kind: 'friend', icon: '🤝', tone: 'positive', key: 'player.reaction.friend' },
  { kind: 'teacher', icon: '🧠', tone: 'positive', key: 'player.reaction.teacher' },
  { kind: 'funny', icon: '😂', tone: 'positive', key: 'player.reaction.funny' },
  { kind: 'danger', icon: '🔥', tone: 'neutral', key: 'player.reaction.danger' },
  { kind: 'chaos', icon: '🎲', tone: 'neutral', key: 'player.reaction.chaos' },
  { kind: 'lucky', icon: '🍀', tone: 'neutral', key: 'player.reaction.lucky' },
  { kind: 'annoy', icon: '😡', tone: 'negative', key: 'player.reaction.annoy' },
  { kind: 'throw', icon: '💩', tone: 'negative', key: 'player.reaction.throw' },
  { kind: 'turtle', icon: '🐢', tone: 'negative', key: 'player.reaction.turtle' },
  { kind: 'toxic', icon: '🥶', tone: 'negative', key: 'player.reaction.toxic' },
];

/** Сколько реакций показываем кратко на самой карточке. */
export const CARD_REACTIONS_TOP_N = 4;

/** Кегль иконок по числу чипов: чем больше реакций, тем компактнее. */
const CARD_REACTION_EMOJI_REM = [0, 2.1, 1.8, 1.55, 1.35];

export interface PlayerReactionsPayload {
  available: boolean;
  counts?: Record<string, number>;
  my?: string[];
  total?: number;
}

/** Заполняет все `.pc-reactions` в root сводкой реакций (пусто — блок скрыт). */
export function paintCardReactions(
  payload: PlayerReactionsPayload | null,
  root: ParentNode = document,
  playerId?: number | null,
): void {
  const hosts = root.querySelectorAll<HTMLElement>('.pc-reactions');
  if (hosts.length === 0) return;

  const total = payload?.total ?? 0;
  const counts = payload?.counts ?? {};

  const active = payload?.available && total > 0
    ? PLAYER_REACTIONS
      .map((reaction, index) => ({ ...reaction, count: counts[reaction.kind] ?? 0, index }))
      .filter((reaction) => reaction.count > 0)
      .sort((a, b) => b.count - a.count || a.index - b.index)
      .slice(0, CARD_REACTIONS_TOP_N)
    : [];

  for (const host of hosts) {
    host.replaceChildren();
    // Пустой блок скрыт через :empty — карточка выглядит как раньше.
    if (active.length === 0) {
      host.style.removeProperty('--rx-emoji');
      continue;
    }

    // Иконки и счётчики: без подписи, только чипы. Размер зависит от их числа.
    const emojiRem = CARD_REACTION_EMOJI_REM[Math.min(active.length, CARD_REACTION_EMOJI_REM.length - 1)] ?? 1.35;
    host.style.setProperty('--rx-emoji', `${emojiRem}rem`);

    const list = document.createElement('div');
    list.className = 'pc-reactions-list';

    for (const reaction of active) {
      const chip = document.createElement('span');
      chip.className = `pc-reaction-chip pc-reaction-chip--${reaction.tone}`;
      chip.title = t(reaction.key);
      chip.setAttribute('aria-label', `${t(reaction.key)}: ${reaction.count}`);
      if (playerId != null) {
        attachWhoTooltip(chip, 'player', playerId, (who) =>
          (who.reactions[reaction.kind] ?? []).map((p) => p.username).filter(Boolean).join(', ') || null,
        );
      }

      const emoji = document.createElement('span');
      emoji.className = 'pc-reaction-emoji';
      emoji.setAttribute('aria-hidden', 'true');
      emoji.textContent = reaction.icon;

      const count = document.createElement('span');
      count.className = 'pc-reaction-count';
      count.textContent = String(reaction.count);

      chip.append(emoji, count);
      list.appendChild(chip);
    }

    host.appendChild(list);
  }
}
